import type { JiraSpaRoute, Match, RouterContext } from '@atlassian/react-resource-router';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import { projectSettingsCustomerServiceManagementCustomerPermissionsRoute } from '@jira/router-routes__project-settings-customer-service-routes/src/projectSettingsCustomerServiceManagementCustomerPermissionsRoute.tsx';
import { projectSettingsCustomerServiceManagementFeaturesRoute } from '@jira/router-routes__project-settings-customer-service-routes/src/projectSettingsCustomerServiceManagementFeaturesRoute.tsx';
import { projectSettingsCustomerServiceManagementFeedbackRoute } from '@jira/router-routes__project-settings-customer-service-routes/src/projectSettingsCustomerServiceManagementFeedbackRoute.tsx';
import { projectSettingsCustomerServiceManagementSlaRoute } from '@jira/router-routes__project-settings-customer-service-routes/src/projectSettingsCustomerServiceManagementSlaRoute.tsx';
import { projectSettingsCustomerServiceManagementVoiceRoute } from '@jira/router-routes__project-settings-customer-service-routes/src/projectSettingsCustomerServiceManagementVoiceRoute.tsx';
import {
	ENTITY_ID,
	MENU_ID_CSM_WORK_ITEMS,
	MENU_ID_CUSTOMER_SERVICE,
	MENU_ID_CUSTOMER_CONVERSATIONS_ANALYTICS,
	MENU_ID_CUSTOMER_SERVICE_ANALYTICS,
	MENU_ID_CUSTOMER_EXPERIENCES_VIEW_ALL,
	MENU_ID_CUSTOMERS,
	MENU_ID_DASHBOARDS,
	MENU_ID_FOR_YOU,
	MENU_ID_PROJECTS,
	MENU_ID_SD_SETTINGS_ACCESS,
	MENU_ID_SD_SETTINGS_REQUEST_MANAGEMENT,
} from '../../../../constants.tsx';
import type { SelectedPath } from '../../../../types.tsx';

const routes = new Set<RouteName>([
	'customer-service-helpcenter',
	'csm-experiences',
	'csm-for-you',
	'customer-service-helpcenter-analytics',
	'csm-helpcenter-analytics',
	'customer-service-helpcenter-customer-conversations-analytics',
	'csm-helpcenter-customer-conversations-analytics',
	'customer-service-customers',
	'csm-customers',
	'customer-service-customer-details',
	'csm-customer-details',
	'customer-service-organizations',
	'csm-organizations',
	'customer-service-organization-details',
	'csm-organization-details',
	'customer-service-products',
	'csm-products',
	'customer-service-work-items-search',
	'csm-work-item',
	projectSettingsCustomerServiceManagementCustomerPermissionsRoute.name,
	projectSettingsCustomerServiceManagementSlaRoute.name,
	projectSettingsCustomerServiceManagementFeaturesRoute.name,
	projectSettingsCustomerServiceManagementFeedbackRoute.name,
	projectSettingsCustomerServiceManagementVoiceRoute.name,
]);

function convert(routeContext: RouterContext<JiraSpaRoute>): SelectedPath {
	const routeName: RouteName = routeContext.route.name;
	const { match } = routeContext;
	const path: string[] = [];
	return convertRouteToPath({ routeName, match, path });
}

function convertNew(args: { routeName: RouteName; match: Match }): SelectedPath {
	const { routeName, match } = args;
	const path: string[] = [];
	return convertRouteToPath({ routeName, match, path });
}

function convertRouteToPath(args: {
	routeName: RouteName;
	match: Match;
	path: string[];
}): SelectedPath {
	const { routeName, match, path } = args;

	switch (routeName) {
		case 'csm-for-you':
			path.push(MENU_ID_FOR_YOU);
			break;

		case 'customer-service-work-items-search':
			path.push(MENU_ID_CSM_WORK_ITEMS);
			break;

		case 'csm-work-item': {
			const [projectKey] = (match.params?.issueKey || '').split('-'); // For this route to match, the issueKey will never be blank
			return [MENU_ID_PROJECTS, ENTITY_ID.PROJECT(projectKey)];
		}

		case 'customer-service-helpcenter':
		case 'csm-experiences':
			path.push(MENU_ID_CUSTOMER_SERVICE);
			path.push(MENU_ID_CUSTOMER_EXPERIENCES_VIEW_ALL);
			break;

		case 'customer-service-helpcenter-analytics':
		case 'csm-helpcenter-analytics':
			// Analytics appears nested under dashboards
			path.push(MENU_ID_DASHBOARDS);
			path.push(MENU_ID_CUSTOMER_SERVICE_ANALYTICS);
			break;

		case 'customer-service-helpcenter-customer-conversations-analytics':
		case 'csm-helpcenter-customer-conversations-analytics':
			path.push(MENU_ID_DASHBOARDS);
			path.push(MENU_ID_CUSTOMER_CONVERSATIONS_ANALYTICS);
			break;

		case 'customer-service-customers':
		case 'csm-customers':
		case 'customer-service-customer-details':
		case 'csm-customer-details':
		case 'customer-service-organizations':
		case 'csm-organizations':
		case 'customer-service-organization-details':
		case 'csm-organization-details':
		case 'customer-service-products':
		case 'csm-products':
			path.push(MENU_ID_CUSTOMERS);
			break;

		case projectSettingsCustomerServiceManagementCustomerPermissionsRoute.name:
			path.push(MENU_ID_SD_SETTINGS_ACCESS);
			break;

		case projectSettingsCustomerServiceManagementSlaRoute.name:
		case projectSettingsCustomerServiceManagementFeedbackRoute.name:
			path.push(MENU_ID_SD_SETTINGS_REQUEST_MANAGEMENT);
			break;

		case projectSettingsCustomerServiceManagementFeaturesRoute.name:
		case projectSettingsCustomerServiceManagementVoiceRoute.name:
			break;
		default:
	}

	return path;
}

export const customerServiceConverter: {
	routes: Set<RouteName>;
	convert: typeof convert;
	convertNew: typeof convertNew;
} = { routes, convert, convertNew };
